import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import EditIcon from '@mui/icons-material/Edit';

import { ensureDefined } from '@e-socna/utils';
import { getFragmentData, graphql } from '@e-socna/data-layer';
import type {
  FragmentType,
  PaymentMethodId,
  PaymentTermId,
  ValidityPeriodId,
} from '@e-socna/data-layer';

import { SpeedDial } from '../../components/SpeedDial';
import { RightDrawer } from '../../components/RightDrawer';
import {
  PATH_RESOLVABLE_MISSION_TYPE_QUOTATION_HEADER,
  PATH_RESOLVABLE_MISSION_TYPE_QUOTATION_HEADER_EDIT,
} from '../../app/constants/paths.constant';
import { FormVariant } from '../../forms';
import {
  ResolvableMissionTypeForm,
  ResolvableMissionTypeFormTab,
} from '../../forms/ResolvableMissionType';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { QuotationHeader as QuotationHeaderCmp } from '../../components/QuotationHeader';

export const ResolvableMissionTypePage_QuotationHeader_ResolvableMissionType_Fragment =
  graphql(`
    fragment ResolvableMissionTypePage_QuotationHeader_ResolvableMissionType_Fragment on ResolvableMissionType {
      _id
      quotationHeader {
        interventionDate
        modalities
        note
        paymentMethodId
        paymentTermId
        validityPeriodId
      }
      ...ResolvableMissionTypeForm_ResolvableMissionType_Fragment
    }
  `);

interface Props {
  readonly resolvableMissionType: FragmentType<
    typeof ResolvableMissionTypePage_QuotationHeader_ResolvableMissionType_Fragment
  >;
}

export const QuotationHeader = ({
  resolvableMissionType: pResolvableMissionType,
}: Readonly<Props>) => {
  const resolvableMissionType = getFragmentData(
    ResolvableMissionTypePage_QuotationHeader_ResolvableMissionType_Fragment,
    pResolvableMissionType,
  );

  const navigate = useNavigate();

  const { paths, currentPathKey } = useBuildPaths(
    {
      consult: PATH_RESOLVABLE_MISSION_TYPE_QUOTATION_HEADER,
      edit: PATH_RESOLVABLE_MISSION_TYPE_QUOTATION_HEADER_EDIT,
    },
    { id: resolvableMissionType._id },
  );

  const isInEditionMode = currentPathKey === 'edit';
  const leaveEditionMode = useCallback(
    () => void navigate(paths.consult),
    [navigate, paths.consult],
  );

  const { paymentMethodsIdMap, paymentTermsIdMap, validityPeriodsIdMap } =
    useReferencesContext();

  const {
    quotationHeader: {
      paymentMethodId,
      paymentTermId,
      validityPeriodId,
      interventionDate,
      modalities,
      note,
    },
  } = resolvableMissionType;

  const { paymentMethod, paymentTerms, validityPeriod } = ensureDefined({
    paymentMethod: paymentMethodsIdMap[paymentMethodId as PaymentMethodId],
    paymentTerms: paymentTermsIdMap[paymentTermId as PaymentTermId],
    validityPeriod: validityPeriodsIdMap[validityPeriodId as ValidityPeriodId],
  });

  return (
    <>
      <QuotationHeaderCmp
        interventionDate={interventionDate}
        modalities={modalities}
        note={note}
        paymentMethod={paymentMethod.label}
        paymentTerms={paymentTerms.label}
        sx={{ pt: 4 }}
        validityPeriod={validityPeriod.label}
      />
      <SpeedDial
        actions={[
          {
            id: 'editMissionTypeQuotationHeaderParameters',
            icon: <EditIcon />,
            name: 'Modifier Paramétrage Entête Devis',
            to: paths.edit,
          },
        ]}
        title="Actions possibles sur le Paramétrage de l'Entête de Devis du Type de Mission"
      />
      <RightDrawer isOpen={isInEditionMode} onDrawerClose={leaveEditionMode}>
        <ResolvableMissionTypeForm
          formVariant={FormVariant.Modification}
          initialValues={resolvableMissionType}
          onCancel={leaveEditionMode}
          onSuccess={leaveEditionMode}
          resolvableMissionTypeId={resolvableMissionType._id}
          stepIdsSubset={[ResolvableMissionTypeFormTab.QuotationHeader]}
          title="Formulaire de modification du Paramétrage de l'Entête de Devis du Type de Mission"
        />
      </RightDrawer>
    </>
  );
};
